import React, { useMemo, useState } from "react";
import { getFunctions, httpsCallable } from "firebase/functions";
import ButtonComponent from "../ButtonComponent/ButtonComponent";
import "./AdminBulkGuestImporter.scss";

const AdminBulkGuestImporter = ({
  selectedUser,
  onSuccess = () => {},
}) => {
  const [rawText, setRawText] = useState("");
  const [guestSide, setGuestSide] = useState("groom");
  const [isSaving, setIsSaving] = useState(false);
  const [resultMsg, setResultMsg] = useState("");
  const [previewFamilies, setPreviewFamilies] = useState([]);

  const functions = getFunctions(undefined, "us-central1");
  const adminBulkAddGuests = httpsCallable(functions, "adminBulkAddGuests");

  const slug = String(selectedUser?.slug || "").trim();
  const targetUserId = String(selectedUser?.id || "").trim();
  const brideName = String(selectedUser?.bride_name || "Bride").trim();
  const groomName = String(selectedUser?.groom_name || "Groom").trim();

  const guestTypeLabel = useMemo(() => {
    return guestSide === "groom"
      ? `${groomName}'s Guest`
      : `${brideName}'s Guest`;
  }, [guestSide, groomName, brideName]);

  const normalizeAttendance = (value) => {
    const v = String(value || "").trim().toLowerCase();
    return v === "yes" || v === "y" ? "Yes" : "No";
  };

  const cleanLine = (line) => {
    return String(line || "")
      .replace(/\u00A0/g, " ")
      .replace(/[–—]/g, "-")
      .replace(/\t/g, " ")
      .trim();
  };

  const isGuestLine = (line) => {
    return /^(.*?)\s*-\s*(yes|no|y|n)\s*$/i.test(line);
  };

  const parseGuestLine = (line) => {
    const match = String(line || "").match(/^(.*?)\s*-\s*(yes|no|y|n)\s*$/i);

    if (!match) return null;

    const guestName = String(match[1] || "").trim();
    const takingLiquor = normalizeAttendance(match[2]);

    if (!guestName) return null;

    return {
      name: guestName,
      status: "Pending",
      takingLiquor,
    };
  };

  const parseBulkText = (text) => {
    const lines = String(text || "").split("\n");
    const groups = [];

    let currentFamilyName = "";
    let currentGuests = [];

    const pushCurrentFamily = () => {
      const familyName = String(currentFamilyName || "").trim();
      const guests = Array.isArray(currentGuests)
        ? currentGuests.filter((guest) => String(guest?.name || "").trim())
        : [];

      if (familyName && guests.length > 0) {
        groups.push({
          familyName,
          guests,
        });
      }

      currentFamilyName = "";
      currentGuests = [];
    };

    for (const rawLine of lines) {
      const line = cleanLine(rawLine);

      if (!line) {
        if (currentFamilyName && currentGuests.length > 0) {
          pushCurrentFamily();
        }
        continue;
      }

      const lowered = line.toLowerCase();

      if (
        lowered === "family members:" ||
        lowered === "family member:" ||
        lowered === "guests:" ||
        lowered === "guest list:"
      ) {
        continue;
      }

      if (isGuestLine(line)) {
        const guest = parseGuestLine(line);

        if (!guest) continue;

        if (!currentFamilyName) {
          continue;
        }

        currentGuests.push(guest);
        continue;
      }

      if (currentFamilyName && currentGuests.length > 0) {
        pushCurrentFamily();
      }

      currentFamilyName = line.replace(/^family\s*name\s*[:\-]\s*/i, "").trim();
    }

    if (currentFamilyName && currentGuests.length > 0) {
      pushCurrentFamily();
    }

    return groups;
  };

  const handlePreview = () => {
    setResultMsg("");

    const groups = parseBulkText(rawText);

    if (!groups.length) {
      setPreviewFamilies([]);
      setResultMsg(
        "No valid family blocks found. Add a family name first, then guest lines under it."
      );
      return;
    }

    setPreviewFamilies(groups);
    setResultMsg(`Preview ready. ${groups.length} family group(s) found.`);
  };

  const handleExecute = async () => {
    if (!selectedUser) {
      setResultMsg("Selected user details are missing.");
      return;
    }

    if (!slug) {
      setResultMsg("Selected user slug is missing.");
      return;
    }

    if (!targetUserId) {
      setResultMsg("Selected user ID is missing.");
      return;
    }

    const families = parseBulkText(rawText);

    if (!families.length) {
      setResultMsg(
        "No valid family blocks found. Add a family name first, then guest lines under it."
      );
      return;
    }

    try {
      setIsSaving(true);
      setResultMsg("");

      const response = await adminBulkAddGuests({
        targetUserId,
        slug,
        guestType: guestTypeLabel,
        families,
      });

      const data = response?.data || {};

      setResultMsg(
        `Done. Added ${data.addedGuests || 0} guest(s) in ${
          data.createdFamilies || 0
        } family group(s).`
      );

      setRawText("");
      setPreviewFamilies([]);
      onSuccess();
    } catch (error) {
      console.error("Admin bulk add failed:", error);
      setResultMsg(
        error?.message || "Bulk add failed. Please check the Cloud Function."
      );
    } finally {
      setIsSaving(false);
    }
  };

  if (!selectedUser) return null;

  return (
    <div className="adminBulkGuestImporter">
      <div className="adminBulkGuestImporter__head">
        <div>
          <p className="adminBulkGuestImporter__eyebrow">Admin Tool</p>
          <h3 className="adminBulkGuestImporter__title">Add Bulk Guests</h3>
          <p className="adminBulkGuestImporter__subtitle">
            Add one family name, then list all guests under it. Yes or No means
            liquor preference.
          </p>
        </div>
      </div>

      <div className="adminBulkGuestImporter__target">
        <span className="adminBulkGuestImporter__label">Selected Couple</span>
        <strong className="adminBulkGuestImporter__value">
          {selectedUser?.bride_name || "Bride"} &{" "}
          {selectedUser?.groom_name || "Groom"}
        </strong>
      </div>

      <div className="adminBulkGuestImporter__grid">
        <div className="adminBulkGuestImporter__group">
          <label className="adminBulkGuestImporter__fieldLabel">
            Guest Side
          </label>
          <select
            className="adminBulkGuestImporter__select"
            value={guestSide}
            onChange={(e) => setGuestSide(e.target.value)}
            disabled={isSaving}
          >
            <option value="groom">{groomName}'s Guest</option>
            <option value="bride">{brideName}'s Guest</option>
          </select>
        </div>
      </div>

      <div className="adminBulkGuestImporter__group">
        <label className="adminBulkGuestImporter__fieldLabel">
          Paste Guest List
        </label>
        <textarea
          className="adminBulkGuestImporter__textarea"
          value={rawText}
          onChange={(e) => setRawText(e.target.value)}
          placeholder={`Godigamuwa Family
Dakshina Godigamuwa - Yes
Ishara Rupasinghe - No

Dilshan Family
Supun Dilshan - Yes
Sadun Manoj - Yes
Kavindu Ishan - No`}
          disabled={isSaving}
        />
      </div>

      <div className="adminBulkGuestImporter__message">
        <strong>Format:</strong>
        <br />
        1. Put the <strong>family name</strong> on one line
        <br />
        2. Add that family&apos;s guests below as <strong>Name - Yes/No</strong>
        <br />
        3. Leave one empty line, then start the next family
      </div>

      <div className="adminBulkGuestImporter__actions">
        <ButtonComponent
          className="adminBulkGuestImporter__btn adminBulkGuestImporter__btn--ghost"
          onClick={handlePreview}
          disabled={isSaving}
        >
          Preview
        </ButtonComponent>

        <ButtonComponent
          className="adminBulkGuestImporter__btn"
          onClick={handleExecute}
          disabled={isSaving}
        >
          {isSaving ? "Adding..." : "Execute Bulk Add"}
        </ButtonComponent>
      </div>

      {resultMsg && (
        <div className="adminBulkGuestImporter__message">{resultMsg}</div>
      )}

      {previewFamilies.length > 0 && (
        <div className="adminBulkGuestImporter__preview">
          <div className="adminBulkGuestImporter__previewTop">
            <h4 className="adminBulkGuestImporter__previewTitle">Preview</h4>
            <span className="adminBulkGuestImporter__previewCount">
              {previewFamilies.length} families
            </span>
          </div>

          <div className="adminBulkGuestImporter__previewList">
            {previewFamilies.map((family, index) => (
              <div
                key={`${family.familyName}-${index}`}
                className="adminBulkGuestImporter__card"
              >
                <div className="adminBulkGuestImporter__cardHead">
                  <strong>{family.familyName}</strong>
                  <span>{family.guests.length} guest(s)</span>
                </div>

                <ul className="adminBulkGuestImporter__guests">
                  {family.guests.map((guest, guestIndex) => (
                    <li key={`${family.familyName}-${guest.name}-${guestIndex}`}>
                      {guest.name} — {guest.takingLiquor}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminBulkGuestImporter;